<template>
  <Autocomplete
    ref="autocomplete"
    v-bind="$attrs"
    v-on="$listeners"
    v-model="innerValue"
    :items="menuItems"
    :search-input.sync="search"
    :loading="loading"
    :menu-props="mergedMenuProps"
    :item-text="itemText"
    :item-value="itemValue"
    :placeholder="placeholder"
    :hide-no-data="hideNoData"
    :hide-selected="hideSelected"
    :clearable="clearable"
    :return-object="returnObject"
    :multiple="multiple"
    :outlined="outlined"
    :dense="dense"
    :filter="() => true"
    :class="['paginated-autocomplete', $attrs.class]"
    :style="[$attrs.style]"
    @focus="handleFocus"
    @blur="handleBlur"
    @update:search-input="handleSearch"
    @click:clear="clearSearch"
  >
    <!-- ITEM SLOT -->
    <template v-slot:item="slotProps">
      <slot name="item" v-bind="slotProps">
        <!-- default -->
        <v-list-item-content>
          <v-list-item-title>
            <template v-if="slotProps.item && slotProps.item.__isCreateOption">
              {{ createText }} "{{ getText(slotProps.item) }}"
            </template>
            <template v-else>
              {{ getText(slotProps.item) }}
            </template>
          </v-list-item-title>
          <v-list-item-subtitle
            v-if="subtitleKey && slotProps.item && slotProps.item[subtitleKey]"
          >
            {{ slotProps.item[subtitleKey] }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </slot>
    </template>

    <!-- SELECTION SLOT -->
    <template v-if="$scopedSlots.selection" v-slot:selection="slotProps">
      <slot name="selection" v-bind="slotProps" />
    </template>

    <!-- APPEND: infinite scroll -->
    <template v-if="infinite" v-slot:append-item>
      <v-divider />
      <v-list-item>
        <v-list-item-content class="text-center">
          <div v-if="hasMoreItems">
            <v-progress-circular
              v-if="loadingMore"
              size="20"
              indeterminate
              color="primary"
            />
            <div
              v-intersect.quiet="onIntersect"
              style="height: 1px; width: 100%"
            />
          </div>

          <small v-else-if="menuItems.length > 0" class="text--disabled">
            {{ endText }}
          </small>
        </v-list-item-content>
      </v-list-item>
    </template>

    <!-- PREPEND: "Buscando por" -->
    <template v-slot:prepend-item v-if="showSearchInfo">
      <v-list-item key="search-info">
        <v-list-item-content>
          <v-list-item-title class="caption text--secondary">
            Buscando por: "{{ search }}"
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-divider />
    </template>

    <!-- NO DATA -->
    <template v-slot:no-data>
      <v-list-item v-if="showNoData">
        <v-list-item-content class="text-center">
          <small class="text--secondary">{{ noDataText }}</small>
        </v-list-item-content>
      </v-list-item>
    </template>
  </Autocomplete>
</template>

<script>
import { debounce } from 'lodash';
import Autocomplete from '@/components/Default/Autocomplete.vue';

export default {
  name: 'PaginatedAutocomplete',
  inheritAttrs: false,

  components: { Autocomplete },

  props: {
    value: { default: null },

    fetch: { type: Function, required: true },

    itemText: { type: [String, Function], default: 'name' },
    itemValue: { type: String, default: 'id' },
    subtitleKey: { type: String, default: '' },

    placeholder: { type: String, default: 'Selecione' },
    noDataText: { type: String, default: 'Nenhum resultado encontrado' },
    endText: { type: String, default: 'Todos os itens foram carregados' },

    initialSearch: { type: String, default: '' },
    minSearchChars: { type: Number, default: 3 },
    debounceMs: { type: Number, default: 500 },
    loadOnFocus: { type: Boolean, default: true },
    openMenuAfterLoad: { type: Boolean, default: false },

    infinite: { type: Boolean, default: true },
    pageSize: { type: Number, default: 20 },

    clearable: { type: Boolean, default: true },
    hideNoData: { type: Boolean, default: false },
    hideSelected: { type: Boolean, default: false },
    returnObject: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    refreshListItems: { type: Boolean, default: false },
    allowCreate: { type: Boolean, default: false },
    commitCreateOnBlur: { type: Boolean, default: true },
    createText: { type: String, default: 'Criar' },

    regular: { type: Boolean, default: false },
    outlined: { type: Boolean, default: false },
    dense: { type: Boolean, default: false },

    menuProps: {
      type: Object,
      default: () => ({
        maxHeight: 304,
        'offset-y': true,
        'nudge-bottom': 10,
      }),
    },

    prependItems: {
      type: Function,
      default: null,
    },
    mapItem: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      innerValue: null,
      items: [],
      search: this.initialSearch,

      loading: false,
      loadingMore: false,

      currentPage: 0,
      hasMoreItems: true,
      currentSearch: this.initialSearch,

      requestSequence: 0,
      initialDataLoaded: false,
      isSelecting: false,
      hasFocus: false,
      shouldOpenMenuAfterLoad: false,
      menuWidth: null,
    };
  },

  computed: {
    mergedMenuProps() {
      const w = this.menuWidth || undefined;

      const base = {
        maxHeight: 304,
        offsetY: true,
        nudgeBottom: 10,
        closeOnContentClick: false,
        ...(this.menuProps || {}),
      };

      const existingClass = base.contentClass || base['content-class'] || '';
      base.contentClass = [
        'paginated-autocomplete__menu',
        existingClass,
      ]
        .filter(Boolean)
        .join(' ');
      base['content-class'] = base.contentClass;

      if (w) {
        base.minWidth = w;
        base.maxWidth = w;
        base['min-width'] = w;
        base['max-width'] = w;
      }

      return base;
    },

    showSearchInfo() {
      return (
        this.search
        && this.search.length >= this.minSearchChars
        && this.items.length > 0
      );
    },

    showNoData() {
      return this.search && this.menuItems.length === 0 && !this.loading;
    },

    prependedItems() {
      return typeof this.prependItems === 'function'
        ? this.prependItems() || []
        : [];
    },

    menuItems() {
      const items = this.currentSearch
        ? this.items
        : this.mergeById(this.items, this.prependedItems);
      return this.createOption ? [this.createOption, ...items] : items;
    },

    createOption() {
      const value = String(this.search || '').trim();
      if (!this.allowCreate || !value || this.hasExactTextMatch(value)) return null;
      return {
        [this.itemValue]: value,
        [typeof this.itemText === 'string' ? this.itemText : 'name']: value,
        __isCreateOption: true,
      };
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(v) {
        this.innerValue = v;
      },
    },

    innerValue(newVal, oldValue) {
      if (!newVal) return;
      if (newVal === oldValue) return;

      if (this.multiple) return;

      this.debouncedSearch.cancel();

      let displayText = '';

      if (this.returnObject) {
        displayText = this.getText(newVal);
      } else {
        const found = this.items.find((item) => {
          const itemVal = typeof this.itemValue === 'function'
            ? this.itemValue(item)
            : item[this.itemValue];

          return itemVal === newVal;
        });
        displayText = found ? this.getText(found) : '';
      }

      this.isSelecting = true;
      this.search = displayText;
      this.currentSearch = displayText;
      this.$nextTick(() => (this.isSelecting = false));
    },

    initialSearch(newVal) {
      this.search = newVal;
      this.currentSearch = newVal;

      if (newVal) {
        this.load(false);
      } else {
        this.loadInitialData(true);
      }
    },

    refreshListItems: {
      handler(v) {
        if (v) {
          this.refreshItems();
          this.$emit('listUpdated');
        }
      },
    },
  },

  created() {
    this.debouncedSearch = debounce(
      this.handleSearchDebounced,
      this.debounceMs,
    );
  },

  mounted() {
    this.seedPrependItems();
    this.updateMenuWidth();
    this.observeInputResize();
    window.addEventListener('resize', this.updateMenuWidth, { passive: true });
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateMenuWidth);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.debouncedSearch.cancel();
  },

  methods: {
    updateMenuWidth() {
      this.$nextTick(() => {
        const wrapper = this.$refs.autocomplete;
        const width = typeof wrapper?.getInputWidth === 'function'
          ? wrapper.getInputWidth()
          : 0;
        if (width > 0) {
          this.menuWidth = width;
        }
      });
    },

    observeInputResize() {
      if (typeof ResizeObserver === 'undefined') return;

      this.$nextTick(() => {
        const wrapper = this.$refs.autocomplete;
        const el = typeof wrapper?.getInputElement === 'function'
          ? wrapper.getInputElement()
          : null;
        if (!el) return;

        this.resizeObserver = new ResizeObserver(() => {
          this.updateMenuWidth();
        });
        this.resizeObserver.observe(el);
      });
    },

    focus() {
      if (this.$refs.autocomplete) {
        this.$refs.autocomplete.focus();
      }
    },

    openMenu() {
      if (this.$refs.autocomplete) {
        this.shouldOpenMenuAfterLoad = this.openMenuAfterLoad;
        this.$refs.autocomplete.openMenu();
      }
    },

    getText(item) {
      if (!item) return '';
      return typeof this.itemText === 'function'
        ? this.itemText(item)
        : item[this.itemText];
    },

    getAvailableItems() {
      return this.mergeById(this.items, this.prependedItems);
    },

    hasExactTextMatch(value) {
      const normalizedValue = String(value).trim().toLowerCase();
      return this.getAvailableItems()
        .some((item) => String(this.getText(item)).trim().toLowerCase() === normalizedValue);
    },

    commitSearchValue() {
      if (!this.allowCreate) return;
      if (!this.commitCreateOnBlur) return;
      const value = String(this.search || '').trim();
      if (!value) return;

      const normalizedValue = value.toLowerCase();
      const existing = this.getAvailableItems()
        .find((item) => String(this.getText(item)).trim().toLowerCase() === normalizedValue);
      const committedValue = existing ? existing[this.itemValue] : value;

      if (this.innerValue === committedValue) return;
      this.innerValue = committedValue;
      this.$emit('input', committedValue);
    },

    async onFocus() {
      if (!this.loadOnFocus) return;
      if (this.loading) return;

      if (this.search && this.search.length >= this.minSearchChars) {
        await this.load(false);
      } else if (!this.search) {
        await this.loadInitialData(true);
      }
    },

    handleFocus() {
      this.hasFocus = true;
      this.updateMenuWidth();
      this.shouldOpenMenuAfterLoad = this.openMenuAfterLoad;
      this.onFocus();
    },

    handleBlur() {
      this.commitSearchValue();
      this.hasFocus = false;
      this.shouldOpenMenuAfterLoad = false;
      this.debouncedSearch.cancel();
      this.search = '';
      this.currentSearch = '';
    },

    async loadInitialData(force = false) {
      if (
        !force
        && this.initialDataLoaded
        && this.items.length > 0
        && !this.search
      ) return;

      this.currentPage = 0;
      this.hasMoreItems = true;

      this.loading = true;
      this.requestSequence += 1;
      const currentRequest = this.requestSequence;

      try {
        const res = await this.fetch(0, this.pageSize, '');
        if (currentRequest !== this.requestSequence) return;
        this.processResponse(res, false, 0, '');
        this.initialDataLoaded = true;
      } catch (e) {
        this.$emit('error', e);
      } finally {
        if (currentRequest === this.requestSequence) {
          this.loading = false;
          this.reopenMenuAfterLoad();
        }
      }
    },

    async load(isLoadMore = false) {
      if (this.loading && !isLoadMore) return;
      if (this.loadingMore && isLoadMore) return;

      this.requestSequence += 1;
      const currentRequest = this.requestSequence;

      if (isLoadMore) {
        if (!this.hasMoreItems) return;
        this.loadingMore = true;
      } else {
        this.loading = true;
        this.items = [];
        this.currentPage = 0;
        this.hasMoreItems = true;
      }

      try {
        const pageToLoad = isLoadMore ? this.currentPage + 1 : 0;
        const res = await this.fetch(
          pageToLoad,
          this.pageSize,
          this.currentSearch,
        );
        if (currentRequest !== this.requestSequence) return;
        this.processResponse(res, isLoadMore, pageToLoad, this.currentSearch);
      } catch (e) {
        this.$emit('error', e);
      } finally {
        if (currentRequest === this.requestSequence) {
          if (isLoadMore) this.loadingMore = false;
          else {
            this.loading = false;
            this.reopenMenuAfterLoad();
          }
        }
      }
    },

    processResponse(
      response,
      isLoadMore = false,
      pageToLoad = 0,
      searchTerm = '',
    ) {
      const content = response.content || [];
      const last = !!response.last;

      const processed = [];

      if (
        !isLoadMore
        && pageToLoad === 0
        && !searchTerm
        && typeof this.prependItems === 'function'
      ) {
        processed.push(...(this.prependItems() || []));
      }

      const mapped = this.mapItem ? content.map(this.mapItem) : content;
      processed.push(...mapped);

      if (isLoadMore) {
        this.items = this.mergeById(this.items, processed);
        this.currentPage = pageToLoad;
      } else {
        this.items = this.mergeById([], processed);
        this.currentPage = 0;
      }

      this.hasMoreItems = !last && content.length > 0;
    },

    mergeById(existing, incoming) {
      const map = new Map();
      existing.forEach((i) => map.set(i[this.itemValue], i));
      incoming.forEach((i) => map.set(i[this.itemValue], i));
      return Array.from(map.values());
    },

    seedPrependItems() {
      if (typeof this.prependItems !== 'function') {
        return;
      }
      this.items = this.mergeById(this.items, this.prependItems() || []);
    },

    reopenMenuAfterLoad() {
      if (!this.shouldOpenMenuAfterLoad || !this.hasFocus) return;

      this.shouldOpenMenuAfterLoad = false;
      this.$nextTick(() => {
        if (this.hasFocus) this.openMenu();
      });
    },

    handleSearch(searchTerm) {
      if (this.isSelecting || !this.hasFocus) return;
      this.debouncedSearch(searchTerm);
    },

    handleSearchDebounced(searchTerm) {
      if (!this.hasFocus) return;
      const next = searchTerm || '';

      if (next === this.currentSearch && this.initialDataLoaded) return;
      this.currentSearch = next;

      if (this.currentSearch === '') {
        if (!this.initialDataLoaded) this.loadInitialData(false);
        return;
      }

      if (this.currentSearch.length >= this.minSearchChars) {
        this.load(false);
      }
    },

    clearSearch() {
      this.search = '';
      this.currentSearch = '';
      this.requestSequence += 1;
      this.loadInitialData(true);
    },

    onIntersect(entries) {
      if (!entries || !entries[0]) return;

      if (
        entries[0].isIntersecting
        && this.infinite
        && this.hasMoreItems
        && !this.loading
        && !this.loadingMore
      ) {
        this.load(true);
      }
    },

    refresh() {
      if (
        this.currentSearch
        && this.currentSearch.length >= this.minSearchChars
      ) this.load(false);
      else this.loadInitialData(true);
    },

    refreshItems() {
      this.loadInitialData(true);
    },
  },
};
</script>

<style lang="scss">
.paginated-autocomplete.v-input--dense {
  > .v-input__control > .v-input__slot > .v-select__slot > .v-input__append-inner {
    align-items: center !important;
    align-self: center !important;
    display: flex !important;
    height: 100% !important;
    margin-top: 0 !important;
  }

  .v-input__icon--clear,
  .v-input__icon--append {
    align-items: center !important;
    display: flex !important;
    height: 20px !important;
    justify-content: center !important;
    min-width: 20px !important;
    width: 20px !important;
  }

  .v-input__icon--clear .v-icon,
  .v-input__icon--append .v-icon {
    align-items: center !important;
    display: inline-flex !important;
    font-size: 16px !important;
    height: 20px !important;
    justify-content: center !important;
    line-height: 20px !important;
    width: 20px !important;
  }
}

.paginated-autocomplete__menu {
  box-sizing: border-box !important;
  overflow-x: hidden !important;

  .v-list-item {
    min-width: 0 !important;
  }

  .v-list-item__content {
    min-width: 0 !important;
    overflow: hidden;
  }

  .v-list-item__title,
  .v-list-item__subtitle {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
